<template>
  <v-dialog v-model="dialog" max-width="850">
    <v-card :prepend-icon="mdiAccount" :title="formTitle" class="rounded-lg">
      <v-form ref="form" @submit.prevent>
        <v-card-text class="pt-4">
          <v-container fluid>
            <!-- Section informations -->
            <div class="mb-5">
              <h3 class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t("information") }}
              </h3>
              <v-row>
                <v-col cols="12" md="4" sm="6">
                  <v-text-field
                    :label="$t('name') + ' *'"
                    v-model="editedItem.name"
                    density="comfortable"
                    variant="outlined"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="4" sm="6">
                  <v-text-field
                    :label="$t('description')"
                    v-model="editedItem.description"
                    density="comfortable"
                    variant="outlined"
                    bg-color="white"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="4" sm="6">
                  <v-text-field
                    :label="$t('link')"
                    v-model="editedItem.link"
                    density="comfortable"
                    variant="outlined"
                    :prepend-inner-icon="mdiLinkVariant"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section période -->
            <div class="mb-5">
              <h3 class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t("period") }}
              </h3>
              <v-row>
                <v-col cols="12" md="6" sm="6">
                  <v-locale-provider locale="fr">
                    <v-date-input
                      :label="$t('start-date') + ' *'"
                      v-model="startDate"
                      variant="outlined"
                      density="comfortable"
                      :prepend-inner-icon="mdiCalendarStart"
                    ></v-date-input>
                  </v-locale-provider>
                </v-col>

                <v-col cols="12" md="6" sm="6">
                  <v-locale-provider locale="fr">
                    <v-date-input
                      :label="$t('end-date') + ' *'"
                      v-model="endDate"
                      variant="outlined"
                      density="comfortable"
                      :prepend-inner-icon="mdiCalendarEnd"
                    ></v-date-input>
                  </v-locale-provider>
                </v-col>
              </v-row>
            </div>

            <!-- Section Affichage et Image -->
            <div class="mb-5">
              <h3 class="text-subtitle-1 font-weight-medium mb-3">
                {{ $t("display") }}
              </h3>
              <v-row>
                <v-col cols="12" md="6" sm="6">
                  <p class="text-subtitle-2 font-weight-medium mb-2">
                    {{ $t("position") }} :
                  </p>
                  <v-btn-toggle
                    v-model="editedItem.position"
                    color="#ffb500"
                    rounded
                    variant="outlined"
                    mandatory
                    class="mb-3"
                  >
                    <v-btn :value="1" class="text-body-2 px-4">
                      <v-icon start>{{ mdiArrowUpBold }}</v-icon>
                      {{ $t("top") }}
                    </v-btn>
                    <v-btn :value="2" class="text-body-2 px-4">
                      <v-icon start>{{ mdiArrowDownBold }}</v-icon>
                      {{ $t("bottom") }}
                    </v-btn>
                    <v-btn :value="3" class="text-body-2 px-4">
                      <v-icon start>{{ mdiArrowUpDownBold }}</v-icon>
                      {{ $t("both") }}
                    </v-btn>
                  </v-btn-toggle>

                  <v-switch
                    v-model="editedItem.active"
                    :label="$t('active') || 'Actif'"
                    color="#ffb500"
                    hide-details
                    inset
                    class="mt-3"
                  ></v-switch>
                </v-col>

                <v-col cols="12" md="6" sm="6">
                  <v-file-input
                    :label="$t('upload-image')"
                    v-model="pubFile"
                    accept=".png, .jpg, .jpeg, .gif"
                    show-size
                    truncate-length="25"
                    @change="handleImageChange"
                    :prepend-icon="mdiImagePlus"
                    variant="outlined"
                    density="comfortable"
                  ></v-file-input>

                  <!-- Aperçu de l'image -->
                  <v-card
                    v-if="imagePreview || editedItem.imageData"
                    class="mt-3"
                    variant="outlined"
                  >
                    <v-toolbar density="compact">
                      <v-toolbar-title class="text-subtitle-2">
                        <v-icon start size="small">
                          {{ mdiImageOutline }}
                        </v-icon>
                        {{ $t("image-preview") }}
                      </v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-btn
                        :icon="mdiClose"
                        size="small"
                        @click="clearImagePreview"
                        variant="text"
                        color="grey-darken-1"
                      ></v-btn>
                    </v-toolbar>
                    <v-card-text class="d-flex justify-center pa-4">
                      <v-img
                        v-if="imagePreview"
                        :src="imagePreview"
                        max-height="200"
                        max-width="300"
                        contain
                        class="rounded"
                      ></v-img>
                      <v-img
                        v-else-if="editedItem.imageData"
                        :src="
                          isBase64Image(editedItem.imageData)
                            ? editedItem.imageData
                            : 'data:image/png;base64,' + editedItem.imageData
                        "
                        max-height="200"
                        max-width="300"
                        contain
                        class="rounded"
                      ></v-img>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <div class="text-caption text-medium-emphasis mt-4 mb-2">
              {{ $t("require-fields") }}
            </div>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>

          <v-btn
            :prepend-icon="mdiClose || 'mdi-close'"
            :text="$t('close')"
            variant="outlined"
            class="mr-3"
            @click="$emit('close')"
          ></v-btn>

          <v-btn
            color="#ffb500"
            type="submit"
            :text="$t('save')"
            variant="elevated"
            :prepend-icon="mdiContentSave"
            @click="save"
          ></v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiPencil,
  mdiAccount,
  mdiContentSave,
  mdiClose,
  mdiLinkVariant,
  mdiCalendarStart,
  mdiCalendarEnd,
  mdiArrowUpBold,
  mdiArrowDownBold,
  mdiArrowUpDownBold,
  mdiImagePlus,
  mdiImageOutline,
} from "@mdi/js";
import { mapState } from "vuex";

export default {
  name: "AddEditPubs",
  props: {
    modelDialog: Boolean,
    customer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mdiPencil,
      mdiAccount,
      mdiContentSave,
      mdiClose,
      mdiLinkVariant,
      mdiCalendarStart,
      mdiCalendarEnd,
      mdiArrowUpBold,
      mdiArrowDownBold,
      mdiArrowUpDownBold,
      mdiImagePlus,
      mdiImageOutline,
      pubFile: null,
      imagePreview: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelDialog;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    ...mapState({
      editedId: (state) => state.pubs.editedId,
    }),
    startDate: {
      get() {
        return new Date(this.$store.state.pubs.editedItem.startDate);
      },
      set(value) {
        this.$store.commit("pubs/setEditedStartDate", value);
      },
    },
    endDate: {
      get() {
        return new Date(this.$store.state.pubs.editedItem.endDate);
      },
      set(value) {
        this.$store.commit("pubs/setEditedEndDate", value);
      },
    },
    editedItem: {
      get() {
        return this.$store.state.pubs.editedItem;
      },
      set(value) {
        this.$store.commit("pubs/setEditedItem", value);
      },
    },
    formTitle() {
      return this.editedId === -1
        ? this.$t("admin.new-pub")
        : this.$t("admin.edit-pub");
    },
  },
  created() {
    // Réinitialiser l'aperçu d'image au chargement du composant
    this.imagePreview = null;
  },
  watch: {
    // Observer les changements de dialogue pour réinitialiser l'aperçu
    dialog(val) {
      if (val) {
        this.imagePreview = null;
      }
    },
  },
  methods: {
    isBase64Image(str) {
      // Vérifie si la chaîne est déjà au format data URL (commence par "data:image/")
      return str && typeof str === "string" && str.startsWith("data:image/");
    },
    handleImageChange(file) {
      if (file) {
        // Convertir le fichier en base64 pour la prévisualisation
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result; // Ceci sera un data URL au format "data:image/png;base64,..."
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },
    clearImagePreview() {
      this.imagePreview = null;
      this.pubFile = null;
      this.$store.commit("pubs/setEditedImageData", null);
    },
    close() {
      this.pubFile = null;
      this.imagePreview = null;
      this.$emit("close");
    },
    save() {
      const valid = this.$refs.form.isValid;
      if (valid) {
        if (this.editedId === -1) {
          this.$store
            .dispatch("pubs/saveEditedItem", this.pubFile)
            .then(() => {
              this.$store.dispatch("showSnackbar", {
                message: this.$t("alert.success.pub-create"),
                color: "success",
              });
              this.close();
            })
            .catch(() => {
              this.$store.dispatch("showSnackbar", {
                message: this.$t("alert.error.pubs-create"),
                color: "error",
              });
              this.close();
            });
        } else {
          this.$store
            .dispatch("pubs/updateEditedItem", this.pubFile)
            .then(() => {
              this.$store.dispatch("showSnackbar", {
                message: this.$t("alert.success.pubs-update"),
                color: "success",
              });
              this.close();
            })
            .catch(() => {
              this.$store.dispatch("showSnackbar", {
                message: this.$t("alert.error.pubs-update"),
                color: "error",
              });
              this.close();
            });
        }
      }
    },
  },
};
</script>
